import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import { produce } from 'immer';
import { useState } from 'react';
import { OrdersAndTransactionsTabs, formatOrders, formatTransactions } from '.';
import { getSkip } from '../../../../../../common/widgets/Grid';
import { config } from '../../../../config';
import {
  OrderItemModel,
  TransactionItemModel,
} from '../../../../models/Orders';
import { OrdersAndTransactionsTabNav } from './OrdersAndTransactionsTabNav';
import { OrdersGrid } from './OrdersGrid';
import { TransactionsGrid } from './TransactionsGrid';
import { OrderStatuses } from '../../../../enums';

interface OrdersData {
  data?: OrderItemModel[];
  total: number;
  dataState: DataState;
}

interface TransactionsData {
  data?: TransactionItemModel[];
  total: number;
  dataState: DataState;
}

interface OrdersAndTransactionsForDashboardState {
  activeTab: OrdersAndTransactionsTabs;
  orders: OrdersData;
  transactions: TransactionsData;
  showErrorPanel?: boolean;
}

export const OrdersAndTransactionsForDashboard = () => {
  const ordersInitialState: any = {
    total: 0,
    dataState: {
      skip: 0,
      take: config.grid.defaultPageSize,
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.NotEqualTo,
            value: OrderStatuses.Failed,
          },
        ],
      },
      sort: [{ field: 'dateAdded', dir: 'desc' }],
    },
  };
  const transactionsInitialState: any = {
    total: 0,
    dataState: {
      skip: 0,
      take: config.grid.defaultPageSize,
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.NotEqualTo,
            value: OrderStatuses.Failed,
          },
        ],
      },
      sort: [{ field: 'dateAdded', dir: 'desc' }],
    },
  };
  const [state, setState] = useState<OrdersAndTransactionsForDashboardState>({
    activeTab: OrdersAndTransactionsTabs.Orders,
    orders: ordersInitialState,
    transactions: transactionsInitialState,
  });

  const onTabChange = (tab: OrdersAndTransactionsTabs) => {
    setState(
      produce(d => {
        d.activeTab = tab;
      }),
    );
  };

  const onOrdersDataReceived = (
    result: DataResult,
    showErrorPanel?: boolean,
  ) => {
    setState(
      produce(d => {
        d.orders.data = formatOrders(result.data);
        d.orders.total = result.total;
        d.showErrorPanel = showErrorPanel;
      }),
    );
  };

  const onTransactionsDataReceived = (result: DataResult) => {
    setState(
      produce(d => {
        d.transactions.data = formatTransactions(result.data);
        d.transactions.total = result.total;
      }),
    );
  };

  const onOrdersDataStateChange = (dataState: DataState) => {
    setState(
      produce(d => {
        d.orders.dataState = {
          ...dataState,
          skip: getSkip(d.orders.dataState, dataState),
        };
      }),
    );
  };

  const onTransactionsDataStateChange = (dataState: DataState) => {
    setState(
      produce(d => {
        d.transactions.dataState = {
          ...dataState,
          skip: getSkip(d.transactions.dataState, dataState),
        };
      }),
    );
  };

  return (
    <>
      <OrdersAndTransactionsTabNav
        activeTab={state.activeTab}
        onTabChange={tab => onTabChange(tab)}
      />
      {state.activeTab === OrdersAndTransactionsTabs.Orders && (
        <OrdersGrid
          showFiltersAndReport={false}
          orders={{
            ...state.orders,
            onDataReceived: onOrdersDataReceived,
            onDataStateChange: onOrdersDataStateChange,
          }}
          showErrorPanel={state.showErrorPanel}
          isForDashboard={true}
        />
      )}
      {state.activeTab === OrdersAndTransactionsTabs.Transactions && (
        <TransactionsGrid
          showFiltersAndReport={false}
          transactions={{
            ...state.transactions,
            onDataReceived: onTransactionsDataReceived,
            onDataStateChange: onTransactionsDataStateChange,
          }}
          showErrorPanel={state.showErrorPanel}
          isForDashboard={true}
        />
      )}
    </>
  );
};
